import { Component, OnDestroy, OnInit } from '@angular/core';
import { ApplyColumnStateParams, ColDef, GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { ToastrService } from 'ngx-toastr';
import { lastValueFrom } from 'rxjs';
import { AgGridColumns, AgGridFilterParams, AgGridOptionsBuilder } from '../../../Compliance/AgGrid';
import { AgGridLinkCellRendererComponent, AgGridLinkCellRendererParams } from '../../../Compliance/AgGrid/CellRenderers/agGridLinkCellRenderer.component';
import { EntityImportRepository } from '../../../Compliance/Repositories/entityImport.repository';
import { BusyIndicatorService } from '../../../Busy-Indicator';
import { HelpService } from '../../../UI-Lib/Help-Tooltip';
import { WeissmanDateTimeFormatPipe } from '../../../UI-Lib/Pipes';
import { RestrictService, Roles } from '../../../Common/Permissions/restrict.service';
import { UpgradeNavigationServiceHandler } from '../../../Common/Routing/upgrade-navigation-handler.service';
import {
    ImportRecalculationErrorsListGridActionCellRendererComponent,
    ImportRecalculationErrorsListGridActionCellRendererParams
} from './agGridActionCellRenderer.component';
import { IMPORT_RECALCULATION_ERRORS_LIST_HELP } from './importRecalculationErrorsList.component.help';

const RECALCULATION_STATUS_TEXT: { [key: number]: string } = {
    [Compliance.ImportFileActivityErrorRecalculationStatusEnum.Blank]: '',
    [Compliance.ImportFileActivityErrorRecalculationStatusEnum.Queued]: 'Queued',
    [Compliance.ImportFileActivityErrorRecalculationStatusEnum.Running]: 'Running',
    [Compliance.ImportFileActivityErrorRecalculationStatusEnum.Error]: 'Error'
};

@Component({
    selector: 'import-recalculation-errors-list',
    templateUrl: './importRecalculationErrorsList.component.html',
    styleUrls: ['./importRecalculationErrorsList.component.scss'],
    standalone: false
})
export class ImportRecalculationErrorsListComponent implements OnInit, OnDestroy {
    constructor(
        private readonly _entityImportRepository: EntityImportRepository,
        private readonly _dateTimePipe: WeissmanDateTimeFormatPipe,
        private readonly _busyService: BusyIndicatorService,
        private readonly _helpService: HelpService,
        private readonly _restrictService: RestrictService,
        private readonly _routerService: UpgradeNavigationServiceHandler,
        private readonly _toastr: ToastrService) {
    }

    gridId: System.Guid = '9E9C0E0E-6E9B-4E6E-9A9C-3B2C7C8F0A11';
    isInitialized: boolean = false;
    canEdit: boolean = false;
    includeAcknowledged: boolean = false;
    gridOptions: GridOptions = new AgGridOptionsBuilder()
        .withContext(this)
        .withColumnPinning()
        .withLoadingOverlay()
        .withColumnResize()
        .withSort()
        .withTextSelection()
        .build();
    gridApi: GridApi;

    private _sourceData: Compliance.ImportFileActivityErrorModel[];
    private _pollHandle: ReturnType<typeof setInterval>;

    get refreshing(): boolean {
        return this._refreshing;
    }
    private _refreshing: boolean;

    ngOnInit(): void {
        this._helpService.setContent(IMPORT_RECALCULATION_ERRORS_LIST_HELP);

        const hasView = this._restrictService.isInRole(Roles.ADMINOPERATIONSVIEW);
        const hasEdit = this._restrictService.isInRole(Roles.ADMINOPERATIONSEDIT);

        if (!hasView && !hasEdit) {
            this.navigateToUnauthorized();
            return;
        }

        this.canEdit = hasEdit;

        this.isInitialized = true;
    }

    ngOnDestroy(): void {
        this._stopPolling();
    }

    onAgGridReady(event: GridReadyEvent): void {
        this.gridApi = event.api;

        const columns: ColDef[] = [
            {
                headerName: 'Import File Name',
                field: 'importFileDisplayName',
                width: AgGridColumns.textColumnLargeWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                cellRenderer: AgGridLinkCellRendererComponent,
                cellRendererParams: {
                    getSref: (params: AgGridLinkCellRendererParams) => {
                        const row = params.data as Compliance.ImportFileActivityErrorModel;
                        if (!row) {
                            return null;
                        }
                        return { target: 'processSystemImport', options: { importId: row.importFileId } };
                    }
                } as unknown as AgGridLinkCellRendererParams
            },
            {
                headerName: 'Import File ID',
                field: 'importFileId',
                type: 'numericColumn',
                width: AgGridColumns.numericColumnWidth,
                filter: 'agNumberColumnFilter',
                filterParams: AgGridFilterParams.numberFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
                cellClass: 'ag-numeric-cell'
            },
            {
                headerName: 'Import File Activity ID',
                field: 'importFileActivityId',
                type: 'numericColumn',
                width: AgGridColumns.numericColumnWidth,
                filter: 'agNumberColumnFilter',
                filterParams: AgGridFilterParams.numberFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
                cellClass: 'ag-numeric-cell'
            },
            {
                headerName: 'Import File Transfer ID',
                field: 'importFileTransferId',
                type: 'numericColumn',
                width: AgGridColumns.numericColumnWidth,
                filter: 'agNumberColumnFilter',
                filterParams: AgGridFilterParams.numberFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
                cellClass: 'ag-numeric-cell'
            },
            {
                headerName: 'Error Occurred Date',
                field: 'errorOccurredDate',
                width: AgGridColumns.dateColumnWidth,
                filter: 'agDateColumnFilter',
                filterParams: AgGridFilterParams.dateFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.dateFloatingFilterParams,
                valueFormatter: (params) => params.value ? this._dateTimePipe.transform(params.value, true) : ''
            },
            {
                headerName: 'Recalculation Status',
                field: 'recalculationStatus',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                valueFormatter: (params) => RECALCULATION_STATUS_TEXT[params.value] ?? ''
            },
            {
                headerName: 'Acknowledge Status',
                field: 'isAcknowledged',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                valueFormatter: (params) => (params.value ? 'Acknowledged' : 'Unacknowledged'),
                hide: true
            },
            {
                headerName: 'Acknowledge Date',
                field: 'acknowledgedDate',
                width: AgGridColumns.dateColumnWidth,
                filter: 'agDateColumnFilter',
                filterParams: AgGridFilterParams.dateFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.dateFloatingFilterParams,
                valueFormatter: (params) => params.value ? this._dateTimePipe.transform(params.value, true) : '',
                hide: true
            },
            {
                headerName: 'Acknowledged By',
                field: 'acknowledgedByUserName',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                hide: true
            },
            {
                headerName: '',
                field: 'actions',
                pinned: 'right',
                width: AgGridColumns.getActionColumnWidth(1),
                minWidth: AgGridColumns.getActionColumnWidth(1),
                maxWidth: AgGridColumns.getActionColumnWidth(1),
                suppressSizeToFit: true,
                suppressAutoSize: true,
                resizable: false,
                suppressColumnsToolPanel: true,
                lockPinned: true,
                sortable: false,
                cellRenderer: ImportRecalculationErrorsListGridActionCellRendererComponent,
                cellRendererParams: {
                    canEdit: this.canEdit,
                    recalculate: this._recalculate.bind(this)
                } as ImportRecalculationErrorsListGridActionCellRendererParams
            }
        ];

        const defaultSortModel: ApplyColumnStateParams = {
            state: [{ colId: 'errorOccurredDate', sort: 'desc' }],
            defaultState: { sort: null }
        };

        this.gridApi.setGridOption('columnDefs', columns);
        this.gridApi.applyColumnState(defaultSortModel);
        this.gridApi.sizeColumnsToFit();

        void this.refresh();
    }

    async refresh(silent: boolean = false): Promise<void> {
        if (!this.gridApi) {
            return;
        }

        this._refreshing = true;

        if (!silent) {
            this.gridApi.setGridOption('loading', true);
        }

        try {
            this._sourceData = await lastValueFrom(this._entityImportRepository.getImportFileActivityErrors(this.includeAcknowledged));
            this.gridApi.setGridOption('rowData', this._sourceData);
            this._updatePolling();
        } finally {
            this._refreshing = false;

            if (!silent) {
                this.gridApi.setGridOption('loading', false);
            }
        }
    }

    onIncludeAcknowledgedChange(): void {
        void this.refresh();
    }

    navigateToUnauthorized(): void {
        this._routerService.go('unauthorizedAccess', {});
    }

    private async _recalculate(params: ImportRecalculationErrorsListGridActionCellRendererParams): Promise<void> {
        const row = params.data as Compliance.ImportFileActivityErrorModel;
        if (!row) {
            return;
        }

        const busyRef = this._busyService.show({ message: 'Starting recalculation' });

        try {
            await lastValueFrom(this._entityImportRepository.recalculateImportFileTransfer(row.importFileActivityId));
            this._toastr.success('Recalculation started. The status will update as the long-running process progresses.');
        } finally {
            busyRef.hide();
        }

        await this.refresh();
    }

    private _updatePolling(): void {
        const hasActiveRecalculation = (this._sourceData || []).some(x =>
            x.recalculationStatus === Compliance.ImportFileActivityErrorRecalculationStatusEnum.Queued ||
            x.recalculationStatus === Compliance.ImportFileActivityErrorRecalculationStatusEnum.Running);

        if (hasActiveRecalculation) {
            this._startPolling();
        } else {
            this._stopPolling();
        }
    }

    private _startPolling(): void {
        if (this._pollHandle) {
            return;
        }

        this._pollHandle = setInterval(() => void this.refresh(true), 5000);
    }

    private _stopPolling(): void {
        if (this._pollHandle) {
            clearInterval(this._pollHandle);
            this._pollHandle = null;
        }
    }
}
